import { Icon } from "@/components/lib";
import { useState } from "react";

type Props = {
  maxFiles?: number;
  onChange: (files: File[] | { type: string; url: string }[]) => void;
  defaultFiles?: File[] | { type: string; url: string }[];
  onDelete?: (
    file: File | { type: string; url: string },
    index: number
  ) => void;
  accept?: string;
};

export default function ImagesUpload({
  maxFiles = 1,
  onChange,
  defaultFiles,
  onDelete,
  accept = "image/*"
}: Props) {
  const [files, setFiles] = useState(defaultFiles);
  const [warning, setWarning] = useState("");

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []).map(
      (file: File) => ({
        type: file.type.startsWith("video/") ? "video" : "image",
        url: URL.createObjectURL(file)
      })
    );
    processFiles(selectedFiles);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files || []).map(
      (file: File) => ({
        type: file.type.startsWith("video/") ? "video" : "image",
        url: URL.createObjectURL(file)
      })
    );
    processFiles(droppedFiles);
  };

  const processFiles = (newFiles) => {
    setWarning("");
    const validFiles = newFiles.filter(
      (file) => file.type === "image" || file.type === "video"
    );

    if (validFiles.some((file) => file.url.includes("blob:null"))) {
      setWarning("Please select a valid file.");
      return;
    }

    if (files.length + validFiles.length > maxFiles) {
      setWarning("Maximum of " + maxFiles + " files allowed.");
      return;
    }

    const allFiles: any = [...files, ...validFiles];

    onChange(allFiles);

    setFiles(allFiles);
  };

  const handleRemoveFile = (index) => {
    const newFiles: any = files.filter((_, i) => i !== index);
    const file = files[index];
    setFiles(newFiles);
    onChange(newFiles);

    onDelete && onDelete(file, index);
  };

  return (
    <div
      className="border-2 border-dashed border-gray-300 p-6 rounded-2xl flex flex-col items-center text-center"
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <label className="cursor-pointer">
        <input
          type="file"
          className="hidden"
          accept={accept}
          multiple={maxFiles > 1}
          disabled={files.length >= maxFiles}
          onChange={handleFileChange}
        />
        <div className="flex flex-col items-center">
          <div className="bg-gray-100 p-3 rounded-full">
            <Icon image="upload" size={20} />
          </div>
          <p className="text-gray-600 mt-2">
            Drag & drop files or{" "}
            <span className="text-blue-500 underline">browse files</span>
          </p>
          <p className="text-gray-400 text-sm">Max {maxFiles} file(s)</p>
        </div>
      </label>

      <div className="flex flex-wrap justify-center gap-2 mt-4 md:w-[550px]">
        {files.length > 0 &&
          files.map((file: any, index) => (
            <div key={index} className="relative">
              {file.type === "image" ? (
                <img
                  src={file.url}
                  alt="File"
                  className="w-32 h-32 object-cover rounded"
                />
              ) : (
                <video
                  src={file.url}
                  controls
                  className="w-32 h-32 object-cover rounded"
                />
              )}
              <div className="absolute inset-0 h-full w-full bg-transparent flex items-start justify-end opacity-0 hover:opacity-100">
                <button
                  onClick={() => handleRemoveFile(index)}
                  type="button"
                  className="text-red-600 bg-white bg-opacity-90 p-2"
                >
                  <Icon image="trash" size={20} />
                </button>
              </div>
            </div>
          ))}
      </div>

      {warning && <p className="text-red-500 my-2">{warning}</p>}
    </div>
  );
}
